// Типизированные обёртки REST-эндпоинтов backend (/api/v1). Контракты — backend/src.
import { api } from './client';
import type {
  Attachment,
  AttachmentType,
  ListDetail,
  ListSummary,
  LoginResponse,
  MarkMode,
  Member,
  RawItem,
  RawList,
  RefreshResponse,
  Urgency,
  User,
} from '@/types/domain';

export const authApi = {
  login: (body: { phone: string; password: string; deviceId?: string; model?: string }) =>
    api.post<LoginResponse>('/auth/login', body, { auth: false }),
  refresh: (refreshToken: string) =>
    api.post<RefreshResponse>('/auth/refresh', { refreshToken }, { auth: false, retryOn401: false }),
  logout: (refreshToken: string) =>
    api.post<{ ok: boolean }>('/auth/logout', { refreshToken }, { auth: false }),
  me: () => api.get<User>('/auth/me'),
};

export const householdApi = {
  members: () => api.get<Member[]>('/household/members'),
};

export interface CreateAttachmentBody {
  type: AttachmentType;
  body?: string;
  url?: string;
  mime?: string;
  sizeBytes?: number;
  durationMs?: number;
  transcript?: string;
  clientId?: string;
}

export const attachmentsApi = {
  list: (itemId: string) => api.get<Attachment[]>(`/items/${itemId}/attachments`),
  create: (itemId: string, body: CreateAttachmentBody) =>
    api.post<Attachment>(`/items/${itemId}/attachments`, body),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/attachments/${id}`),
  transcribe: (id: string) =>
    api.post<{ id: string; transcriptStatus: string }>(`/attachments/${id}/transcribe`),
};

export const mediaApi = {
  presignUpload: (body: { kind: 'photo' | 'voice'; mime: string; sizeBytes: number }) =>
    api.post<{ url: string; headers: Record<string, string>; s3Key: string; expiresIn: number }>(
      '/media/presign-upload',
      body,
    ),
  presignDownload: (s3Key: string) =>
    api.post<{ url: string; expiresIn: number }>('/media/presign-download', { s3Key }),
};

export interface CreateListBody {
  title: string;
  urgency?: Urgency;
  adultsOnly?: boolean;
  markMode?: MarkMode;
  clientId?: string;
}
export interface UpdateListBody {
  title?: string;
  urgency?: Urgency;
  pinned?: boolean;
  archived?: boolean;
  markMode?: MarkMode;
  rollupDirectOnly?: boolean;
  adultsOnly?: boolean;
}

export const listsApi = {
  list: (archived = false) =>
    api.get<ListSummary[]>(`/lists${archived ? '?archived=true' : ''}`),
  get: (id: string) => api.get<ListDetail>(`/lists/${id}`),
  create: (body: CreateListBody) => api.post<RawList>('/lists', body),
  update: (id: string, body: UpdateListBody) => api.patch<RawList>(`/lists/${id}`, body),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/lists/${id}`),
  checkAll: (id: string, done: boolean) =>
    api.post<{ ok: boolean }>(`/lists/${id}/check-all`, { done }),
  getAssignees: (id: string) => api.get<string[]>(`/lists/${id}/assignees`),
  setAssignees: (id: string, userIds: string[]) =>
    api.put<string[]>(`/lists/${id}/assignees`, { userIds }),
};

export interface CreateItemBody {
  text: string;
  parentId?: string;
  urgency?: Urgency;
  afterItemId?: string;
  clientId?: string;
}
export interface UpdateItemBody {
  text?: string;
  urgency?: Urgency;
  baseVersion?: number;
}
export interface MoveItemBody {
  parentId?: string | null;
  afterItemId?: string;
}

export const itemsApi = {
  create: (listId: string, body: CreateItemBody) =>
    api.post<RawItem>(`/lists/${listId}/items`, body),
  update: (id: string, body: UpdateItemBody) => api.patch<RawItem>(`/items/${id}`, body),
  remove: (id: string) => api.delete<{ ok: boolean; removed: number }>(`/items/${id}`),
  check: (id: string, done: boolean) => api.post<RawItem>(`/items/${id}/check`, { done }),
  checkPersonal: (id: string, done: boolean) =>
    api.post<{ userId: string; checkedAt: string | null }[]>(`/items/${id}/check-personal`, { done }),
  move: (id: string, body: MoveItemBody) => api.post<RawItem>(`/items/${id}/move`, body),
  setAssignees: (id: string, userIds: string[]) =>
    api.put<string[]>(`/items/${id}/assignees`, { userIds }),
};
